import { Alert, Grid, Skeleton, Stack, useMediaQuery } from "@mui/material"
import { SkipassSolid } from "@skistarab/icons"
import theme from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import { PageContainer, SrOnly } from "@/components"
import { CategoryCard } from "@/components/CategoryCard"
import {
  FyiWidget,
  PassSearch,
  DateRangeAdvisoryBanners,
  PriceSummaryDrawer,
} from "@/features"
import { BannersList } from "@/features/Banners"
import { useDataLayer } from "@/features/DataLayer"
import { PartnerCodeDisplay } from "@/features/PartnerCodeDisplay"
import { PartnerCodeInput } from "@/features/PartnerCodeInput"
import { usePrerenderSignal, useSelectedResort } from "@/hooks"
import { useDateParam, useResortParam } from "@/hooks/useSkipassFlow"
import { useResorts, useWebCategories } from "@/queries/hooks"

type StartPageProps = {
  onCategorySelect: (categoryId: number) => void
}

const StartPage = ({ onCategorySelect }: StartPageProps) => {
  const { t } = useTranslation()

  const { resortExternalId } = useResortParam()
  const { startDate } = useDateParam()
  const pushProductBlockEvent = useDataLayer("pass_product_block")

  const {
    selectedResort,
    isLoading: isResortLoading,
    isError: isResortsError,
  } = useSelectedResort()

  const { data: resorts, isLoading: isResortsLoading } = useResorts()

  const {
    data: webCategories,
    isError: isWebCategoriesError,
    isLoading: isWebCategoriesLoading,
  } = useWebCategories(resortExternalId, startDate)

  const isCategoriesReady =
    !isWebCategoriesLoading && webCategories !== undefined
  usePrerenderSignal(isCategoriesReady)

  const isTablet = useMediaQuery(theme.breakpoints.up("md"))
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"))

  const headline = selectedResort?.name
    ? `${t("pageTitle")} ${selectedResort.name}`
    : t("pageTitle")

  if (isResortsError) {
    return (
      <PageContainer headline={headline} headlineLoading={isResortLoading}>
        <Alert severity="error" data-testid="data-fetch-error">
          {t("resortsFetchError")}
        </Alert>
      </PageContainer>
    )
  }

  return (
    <PageContainer
      headline={headline}
      headlineLoading={isResortLoading}
      leftIcon={<SkipassSolid />}
    >
      {isWebCategoriesError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("productsFetchError")}
        </Alert>
      )}

      <PassSearch resorts={resorts} isResortsLoading={isResortsLoading} />
      <Stack direction={"row"} alignItems={"start"} spacing={2}>
        <PriceSummaryDrawer />
        <PartnerCodeInput />
      </Stack>
      <PartnerCodeDisplay />

      <Stack mb={4} spacing={2} mt={1}>
        <DateRangeAdvisoryBanners />
        <BannersList />

        {webCategories?.length === 0 && (
          <Alert severity="info" data-testid="no-products-alert">
            {t("noProductsAlert")}
          </Alert>
        )}
        {webCategories && webCategories.length > 0 && (
          <SrOnly>
            <h2 id="category-heading">{t("chooseCategory")}</h2>
          </SrOnly>
        )}
        <Grid
          container
          spacing={2}
          role="group"
          aria-labelledby="category-heading"
        >
          {webCategories?.map((webCategory) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} key={webCategory.id}>
              <CategoryCard
                title={webCategory.name}
                description={webCategory.description}
                imageId={webCategory.imageId}
                features={webCategory.features}
                category={webCategory.category}
                onClick={() => {
                  pushProductBlockEvent({ ProductBlock: webCategory.name })
                  onCategorySelect(webCategory.id)
                }}
              />
            </Grid>
          ))}
          {isWebCategoriesLoading &&
            [...Array(isTablet && !isDesktop ? 3 : 4).keys()].map((index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} key={index}>
                <Skeleton
                  key={index}
                  variant="rounded"
                  width="100%"
                  height={isTablet ? 403 : 112}
                  data-testid={`product-category-skeleton-${index}`}
                />
              </Grid>
            ))}
        </Grid>
      </Stack>
      <FyiWidget page="start" />
    </PageContainer>
  )
}

export default StartPage
